import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardMedia,
  Dialog,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import HeroSection from '../components/common/HeroSection';
import SectionTitle from '../components/common/SectionTitle';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { galleryImages } from '../data/testimonials';

const GalleryPage = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [category, setCategory] = useState('all');

  const filteredImages =
    category === 'all'
      ? galleryImages
      : galleryImages.filter((img) => img.category === category);

  const handleOpen = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const handleClose = () => {
    setLightboxOpen(false);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? filteredImages.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === filteredImages.length - 1 ? 0 : prev + 1));
  };

  const handleCategoryChange = (event, newCategory) => {
    if (newCategory !== null) {
      setCategory(newCategory);
      setCurrentIndex(0);
    }
  };

  return (
    <Box>
      <HeroSection
        title="Gallery"
        subtitle="A glimpse into our learning centre and student achievements"
      />

      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <SectionTitle
            title="Photo Gallery"
            subtitle="Browse through our collection of memories"
          />

          {/* Category Filter */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 5 }}>
            <ToggleButtonGroup
              value={category}
              exclusive
              onChange={handleCategoryChange}
              sx={{
                bgcolor: '#fff',
                borderRadius: 3,
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                '& .MuiToggleButton-root': {
                  px: { xs: 2, md: 4 },
                  py: 1.5,
                  border: 'none',
                  borderRadius: '12px !important',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: '#fff',
                    '&:hover': { bgcolor: 'primary.dark' },
                  },
                },
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="testimonials">Testimonials</ToggleButton>
              <ToggleButton value="olevel">O-Level 2025</ToggleButton>
              <ToggleButton value="classes">Classes</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Image Grid */}
          <Grid container spacing={2}>
            {filteredImages.map((image, index) => (
              <Grid item xs={6} sm={4} md={3} key={image.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    overflow: 'hidden',
                    '&:hover img': {
                      transform: 'scale(1.08)',
                    },
                    '&:hover .gallery-overlay': {
                      opacity: 1,
                    },
                    position: 'relative',
                  }}
                  onClick={() => handleOpen(index)}
                >
                  <CardMedia
                    component="img"
                    image={image.src}
                    alt={image.caption}
                    sx={{
                      height: { xs: 160, sm: 200, md: 220 },
                      objectFit: 'cover',
                      transition: 'transform 0.4s ease',
                    }}
                  />
                  <Box
                    className="gallery-overlay"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: '#fff',
                      p: 1.5,
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {image.caption}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {filteredImages.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No images in this category yet.
              </Typography>
            </Box>
          )}
        </Container>
      </Box>

      {/* Lightbox */}
      <Dialog
        open={lightboxOpen}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: -40,
              right: 0,
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>

          <IconButton
            onClick={handlePrev}
            sx={{
              position: 'absolute',
              left: { xs: -8, md: -50 },
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              zIndex: 1,
            }}
          >
            <NavigateBeforeIcon />
          </IconButton>

          {filteredImages[currentIndex] && (
            <Box
              component="img"
              src={filteredImages[currentIndex].src}
              alt={filteredImages[currentIndex].caption}
              sx={{
                maxWidth: '100%',
                maxHeight: '80vh',
                borderRadius: 2,
                objectFit: 'contain',
              }}
            />
          )}

          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: { xs: -8, md: -50 },
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              zIndex: 1,
            }}
          >
            <NavigateNextIcon />
          </IconButton>
        </Box>
      </Dialog>
    </Box>
  );
};

export default GalleryPage;
